import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../api/hooks/useAuth";
import { useToast } from "../../admin/context/ToastContext";

const OTPVerificationModal = ({ isOpen, onClose, email, onVerifySuccess }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const { forgotPassword, loading } = useAuth(); // Re-use forgotPassword for resending OTP
  const { showToast } = useToast();

  useEffect(() => {
    let interval;
    if (isOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isOpen, timer]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      await forgotPassword(email);
      showToast("OTP resent successfully", "success");
      setTimer(120);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0].focus();
    } catch (err) {
      showToast(err.message || "Failed to resend OTP", "error");
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      showToast("Please enter a 6-digit OTP", "error");
      return;
    }

    onVerifySuccess(otpString);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2001,
        padding: "20px",
        animation: "fadeIn 0.3s ease-out",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          maxWidth: "450px",
          borderRadius: "24px",
          padding: "40px",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.15)",
          position: "relative",
          animation: "slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            background: "#F3F4F6",
            border: "none",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#64748B",
          }}
        >
          ✕
        </button>

        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#0F172A", marginBottom: "8px" }}>
          Reset Password
        </h2>
        <p style={{ color: "#64748B", fontSize: "14px", marginBottom: "32px" }}>
          Enter the 6-digit OTP sent to your email
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label style={{ display: "block", textAlign: "left", fontSize: "14px", fontWeight: "600", color: "#0F172A", marginBottom: "12px" }}>
            Enter OTP
          </label>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                style={{
                  width: "50px",
                  height: "60px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  textAlign: "center",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#0F172A",
                  outline: "none",
                  transition: "all 0.2s",
                  backgroundColor: digit ? "#F8FAFC" : "transparent",
                  borderColor: digit ? "#A855F7" : "#E2E8F0",
                  boxShadow: digit ? "0 0 0 4px rgba(168, 85, 247, 0.1)" : "none",
                }}
              />
            ))}
          </div>
        </div>

        <p style={{ color: "#64748B", fontSize: "13px", marginBottom: "16px" }}>
          OTP sent to <span style={{ fontWeight: "600", color: "#0F172A" }}>{email}</span>
        </p>

        <div style={{ marginBottom: "32px" }}>
          {canResend ? (
            <button
              onClick={handleResend}
              style={{
                background: "none",
                border: "none",
                color: "#A855F7",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Resend OTP
            </button>
          ) : (
            <p style={{ color: "#64748B", fontSize: "14px" }}>
              Resend OTP in <span style={{ color: "#0F172A", fontWeight: "600" }}>{formatTime(timer)}</span>
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              background: "#FFFFFF",
              color: "#0F172A",
              fontWeight: "600",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            Back
          </button>
          <button
            onClick={handleVerify}
            disabled={otp.join("").length !== 6 || loading}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: "12px",
              border: "none",
              background: "linear-gradient(135deg, #A855F7 0%, #9333EA 100%)",
              color: "#FFFFFF",
              fontWeight: "600",
              fontSize: "16px",
              cursor: otp.join("").length !== 6 || loading ? "not-allowed" : "pointer",
              opacity: otp.join("").length !== 6 || loading ? 0.7 : 1,
            }}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationModal;


